/**
 * sb-gp-cart-sync — keeps the deal codes of the GemPages deal blocks (sb-gp-kaching, added by assets/sb-gp-product.js)
 * in step with the cart after every cart change: drawer / cart page + and −, removals, other adds.
 *  • A deal tier's lines carry, in their private `_sb_deal` property, every code of the tier with what it needs
 *    ("codes": { "BUY2": { "the-hoodie-backpack": 2 }, "BUY2GIFT": { "the-hoodie-backpack": 2, "3d-cooling-gel-mask": 1 } }).
 *  • After a cart change the cart is read; a deal code whose needs the cart no longer holds (all lines of each handle
 *    counted, as Shopify counts them) is REMOVED from the cart, and put back once the cart holds them again while a
 *    line of that deal is still in the cart. Shopify's own minimum quantity is not enough: BUY2GIFT ($39 off backpack +
 *    mask, minimum 3 items) left in the cart woke up again on 1 backpack + 2 masks, and on 3 backpacks without the mask.
 *  • Deal codes known without a line naming them (the deal block on this page: window.SbGpDealCodes, set by
 *    sb-gp-product.js; deal lines seen before, remembered in localStorage) are held to the same needs, so a deal whose
 *    lines were all removed, or a deal code typed into the cart, can't discount other lines.
 *  • Merge-safe: every other code (the pop-up's MYSTERY10, a link code, the sb-bundles / quantity-break codes) is kept
 *    as it is; at most 5 codes (Shopify's limit). Runs one at a time, each run reads the cart fresh.
 *  • Loaded by snippets/sb-gp-json (GemPages product pages); safe to load on every page as well (one instance).
 */
import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';

const MAX_CODES = 5;
const MEMORY = 'sb-gp-deal-codes';
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const key = (c) => String(c || '').trim().toUpperCase();

/** @returns {Record<string, Record<string, number>>} */
function remembered() {
  try {
    const v = JSON.parse(window.localStorage.getItem(MEMORY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (e) {
    return {};
  }
}
function remember(map) {
  try {
    window.localStorage.setItem(MEMORY, JSON.stringify(map));
  } catch (e) {
    /* private mode: the lines and the page still carry the needs */
  }
}

/**
 * The cart's code list after the deal codes' needs are applied (null = nothing to change).
 * @param {{ items: any[], discount_codes?: { code: string }[] }} cart
 * @param {Record<string, Record<string, number>>} [known] - deal code → needs, from outside the cart
 * @returns {{ next: string[] | null, named: Record<string, Record<string, number>> }}
 */
export function planDealCodes(cart, known = {}) {
  /** @type {Record<string, Record<string, number>>} */
  const named = {};
  if (!cart || !Array.isArray(cart.items)) return { next: null, named };
  const count = new Map();
  for (const it of cart.items) if (it && it.handle) count.set(it.handle, (count.get(it.handle) || 0) + Number(it.quantity || 0));
  const met = (need) => Object.entries(need || {}).every(([h, q]) => (count.get(h) || 0) >= Number(q));
  for (const it of cart.items) {
    const raw = it && it.properties && it.properties._sb_deal;
    if (!raw) continue;
    let deal;
    try {
      deal = JSON.parse(raw);
    } catch (e) {
      continue;
    }
    const codes = deal && deal.codes;
    if (!codes || typeof codes !== 'object' || Array.isArray(codes)) continue;
    for (const [code, need] of Object.entries(codes)) if (code && need && typeof need === 'object') named[code] = need;
  }
  /** @type {Map<string, { code: string, on: boolean, named: boolean }>} */
  const rules = new Map();
  for (const [code, need] of Object.entries(known || {})) if (code && need && typeof need === 'object') rules.set(key(code), { code, on: met(need), named: false });
  for (const [code, need] of Object.entries(named)) rules.set(key(code), { code, on: met(need), named: true });
  if (!rules.size) return { next: null, named };
  const current = (cart.discount_codes || []).map((d) => d && d.code).filter(Boolean);
  const next = current.filter((c) => {
    const r = rules.get(key(c));
    return !r || r.on;
  });
  for (const [k, r] of rules) if (r.named && r.on && !next.some((c) => key(c) === k) && next.length < MAX_CODES) next.push(r.code);
  const same = next.length === current.length && next.every((c, i) => key(c) === key(current[i]));
  return { next: same ? null : next, named };
}

let epoch = 0; // cart line changes seen (a run's section HTML is stale when another change landed meanwhile)
let chain = Promise.resolve();
let queued = false;

async function run() {
  const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  const memory = remembered();
  const page = /** @type {any} */ (window).SbGpDealCodes || {};
  const { next, named } = planDealCodes(cart, { ...memory, ...page });
  if (Object.keys(named).some((c) => JSON.stringify(memory[c]) !== JSON.stringify(named[c]))) remember({ ...memory, ...named });
  if (!next) return;
  const start = epoch;
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  const deferred = CartDiscountUpdateEvent.createPromise();
  // dispatched from the document: Horizon's cart (drawer and /cart) re-renders from the sections below
  document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: next.map((code) => ({ code })), promise: deferred.promise }));
  try {
    const res = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(','), sections: sections.join(',') }),
    }).then((r) => r.json());
    if (!res || !Array.isArray(res.items)) throw new Error((res && (res.description || res.message)) || 'cart update failed');
    deferred.resolve({
      cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(res),
      // no sections when another change landed meanwhile: the cart then re-fetches itself
      detail: { sections: epoch === start ? res.sections : undefined },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

function schedule() {
  if (queued) return; // the queued run reads the cart when it starts
  queued = true;
  chain = chain
    .then(() => {
      queued = false;
      return run();
    })
    .catch(() => {});
}

const w = /** @type {any} */ (window);
if (!w.__sbGpCartSync) {
  w.__sbGpCartSync = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    epoch++;
    const promise = /** @type {any} */ (event).promise;
    (promise ? promise.catch(() => null) : Promise.resolve()).then(() => schedule());
  });
  // a change made where this script was not loaded (or a code typed in) is put right on the next page: after the
  // deal blocks registered their codes (deferred module scripts run in order; this one comes after sb-gp-product.js)
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => schedule());
  else schedule();
}
